import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck, ChevronRight, Loader2 } from 'lucide-react';
import { BottomSheet } from '@shared/ui/containers/BottomSheet';
import { Card } from '@shared/ui/containers/Card';
import { ListGroup } from '@shared/ui/lists/ListGroup';
import { Button } from '@shared/ui/primitives/Button';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { Banner } from '@shared/ui/feedback/Banner';
import { cn } from '@shared/lib/cn';
import { formatRelative } from '@shared/lib/format';
import { useAuthStore } from '@shared/stores/authStore';
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type Notification,
} from './api';
import { getNotificationTarget } from './notificationTargets';
import { routeNotification } from './routeNotification';
import { useTenantSwitchPrompt } from './tenantSwitchPromptStore';

interface NotificationsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NotificationsSheet({ open, onOpenChange }: NotificationsSheetProps): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);

  const listQuery = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => fetchNotifications({ limit: 30 }),
    enabled: open,
  });

  const markReadMutation = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllMutation = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const items = listQuery.data ?? [];
  const unreadInList = items.filter((n) => !n.is_read).length;

  const subtitle =
    unreadInList > 0
      ? `${unreadInList} unread notification${unreadInList === 1 ? '' : 's'}`
      : "You're all caught up";

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Notifications"
      description={subtitle}
      height="min(72dvh, 560px)"
    >
      {unreadInList > 0 ? (
        <div className="mb-4 flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={markAllMutation.isPending}
            onClick={() => markAllMutation.mutate()}
          >
            <CheckCheck className="h-4 w-4" strokeWidth={1.75} />
            Mark all read
          </Button>
        </div>
      ) : null}

      {listQuery.isLoading ? (
        <div className="flex items-center justify-center gap-2 py-12 text-body text-ink-secondary">
          <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.75} />
          Loading…
        </div>
      ) : null}

      {listQuery.isError ? (
        <Banner variant="danger" className="mb-4">
          Couldn't load notifications. Pull down to try again.
        </Banner>
      ) : null}

      {!listQuery.isLoading && !listQuery.isError && items.length === 0 ? (
        <EmptyState
          icon={<Bell className="h-10 w-10" strokeWidth={1.25} />}
          title="No notifications"
          description="When something needs your attention, it will show up here."
        />
      ) : null}

      {!listQuery.isLoading && items.length > 0 ? (
        <ListGroup className="pb-2">
          {items.map((item) => (
            <NotificationRow
              key={item.notification_id}
              notification={item}
              onOpen={() => {
                if (!item.is_read) {
                  markReadMutation.mutate(item.notification_id);
                }
                setSelectedNotification(item);
              }}
            />
          ))}
        </ListGroup>
      ) : null}

      <BottomSheet
        open={selectedNotification != null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setSelectedNotification(null);
          }
        }}
        title={selectedNotification?.title}
        description={selectedNotification ? formatRelative(selectedNotification.created_at) : undefined}
        height="min(70dvh, 520px)"
      >
        {selectedNotification ? (
          <NotificationDetail
            notification={selectedNotification}
            onOpenTarget={() => {
              const notification = selectedNotification;
              const auth = useAuthStore.getState();
              routeNotification(notification, {
                activeTenantId: auth.tenantId,
                linkedTenants: auth.linkedTenants,
                navigate: (path) => {
                  setSelectedNotification(null);
                  onOpenChange(false);
                  navigate(path);
                },
                requestSwitch: (req) => {
                  setSelectedNotification(null);
                  onOpenChange(false);
                  useTenantSwitchPrompt.getState().request(req);
                },
                openExternal: (url) => window.open(url, '_blank', 'noopener,noreferrer'),
              });
            }}
          />
        ) : null}
      </BottomSheet>
    </BottomSheet>
  );
}

function NotificationRow({
  notification,
  onOpen,
}: {
  notification: Notification;
  onOpen: () => void;
}): JSX.Element {
  const unread = !notification.is_read;

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'group relative flex w-full items-start gap-3 px-4 py-3.5 text-left transition-colors duration-fast',
        unread ? 'bg-primary-50/60 active:bg-primary-50' : 'active:bg-sunken hover:bg-sunken/60',
      )}
    >
      <span
        className={cn(
          'mt-1.5 block h-2 w-2 shrink-0 rounded-full',
          unread ? 'bg-primary-600' : 'bg-hairline-strong/60',
        )}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <p className={cn('truncate text-body text-ink', unread && 'font-semibold')}>
            {notification.title}
          </p>
          <span className="shrink-0 text-caption text-ink-muted">
            {formatRelative(notification.created_at)}
          </span>
        </div>
        <p className="mt-0.5 line-clamp-2 text-body-sm leading-snug text-ink-secondary">
          {notification.message}
        </p>
      </div>
      <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 self-center text-ink-muted" strokeWidth={1.5} />
    </button>
  );
}

function NotificationDetail({
  notification,
  onOpenTarget,
}: {
  notification: Notification;
  onOpenTarget: () => void;
}): JSX.Element {
  const target = getNotificationTarget(notification);

  return (
    <div className="space-y-5 pb-4">
      <Card noGrain className="rounded-2xl bg-sunken/80 px-4 py-4 shadow-none">
        <p className="whitespace-pre-wrap text-body leading-7 text-ink">{notification.message}</p>
      </Card>

      {target ? (
        <Button type="button" variant="primary" size="md" className="w-full" onClick={onOpenTarget}>
          {target.kind === 'external' ? 'Open link' : 'Open related page'}
          <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
        </Button>
      ) : null}
    </div>
  );
}
